import { Add, Close } from "@mui/icons-material";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Stack,
  Typography,
} from "@mui/joy";
import {
  ApiCreditProductAdmin,
  ApiCreditProductCodeBatch,
  ApiCreditProductCodeBatchUpload,
  ApiCreditProductStock,
  ApiCreditSystemAdmin,
} from "griptonite-api-client/types";
import { ReactNode, useMemo, useState } from "react";
import { NumericFormat } from "react-number-format";

import {
  useCreditProductCodeBatchesQuery,
  useCreditProductStockQueries,
  useSetCreditProductStockMutation,
  useWithdrawCreditProductCodeBatchMutation,
} from "@/hooks/credits";
import { useAvailableVenuesQuery } from "@/hooks/venues";
import pluralize from "@/utils/pluralize";
import shortDateFormat from "@/utils/shortDateFormat";

import AddCodesModal from "./AddCodesModal";
import { useUnsavedChanges } from "./UnsavedChanges";

// A gym the reward is offered at. Only its own staff and admins can see or change its stock.
type StockVenue = { uuid: string; name: string; canStock: boolean };

// The reward's saved gyms, with the gym being viewed first. Ticking a gym in the details doesn't
// add it here until it's saved, since the API can't stock a gym the reward isn't offered at.
const useStockVenues = (
  reward: ApiCreditProductAdmin,
  system: ApiCreditSystemAdmin,
  venueUuid: string,
): StockVenue[] | undefined => {
  const { data: staffVenues } = useAvailableVenuesQuery();
  return useMemo(() => {
    if (!staffVenues) {
      return undefined;
    }
    const roles = new Map(staffVenues.map((venue) => [venue.uuid, venue.userRole]));
    return system.venues
      .filter((venue) => reward.venues.includes(venue.uuid))
      .sort((a, b) => Number(b.uuid === venueUuid) - Number(a.uuid === venueUuid))
      .map((venue) => ({
        uuid: venue.uuid,
        name: venue.name,
        canStock: ["ADMIN", "STAFF"].includes(roles.get(venue.uuid) ?? ""),
      }));
  }, [staffVenues, system, reward.venues, venueUuid]);
};

// One gym's row in a group of gyms. A gym the user has no role at is greyed out.
const VenueRow = ({ venue, children }: { venue: StockVenue; children: ReactNode }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={1.5}
    sx={{ minHeight: 40, opacity: venue.canStock ? 1 : 0.6 }}
  >
    <Typography fontWeight="md" noWrap sx={{ flex: 1, minWidth: 0 }}>
      {venue.name}
    </Typography>
    {venue.canStock ? (
      children
    ) : (
      <Typography level="body-sm" textColor="neutral.500">
        Stocked by {venue.name} staff
      </Typography>
    )}
  </Stack>
);

type StickerEdit = { count: number | null; isUnlimited: boolean };

const editToRemaining = (edit: StickerEdit) => (edit.isUnlimited ? null : edit.count);

const StickerCountInput = ({
  edit,
  error,
  onChange,
}: {
  edit: StickerEdit;
  error?: string;
  onChange: (edit: StickerEdit) => void;
}) => (
  <Stack direction="row" alignItems="center" gap={1.5}>
    <NumericFormat
      customInput={Input}
      allowNegative={false}
      decimalScale={0}
      type="tel"
      aria-label="Count"
      error={Boolean(error)}
      disabled={edit.isUnlimited}
      placeholder={edit.isUnlimited ? "Unlimited" : undefined}
      value={edit.isUnlimited ? "" : (edit.count ?? "")}
      onValueChange={(values, sourceInfo) => {
        if (sourceInfo.source === "event") {
          onChange({ ...edit, count: values.floatValue ?? null });
        }
      }}
      sx={{ width: 110 }}
    />
    <Checkbox
      label="Unlimited"
      checked={edit.isUnlimited}
      onChange={(event) => onChange({ ...edit, isUnlimited: event.target.checked })}
    />
  </Stack>
);

// A count at each gym, overwritten on save. The count goes down as climbers redeem the reward,
// so an untouched gym shows the latest count rather than the one it opened with.
const StickerStock = ({
  reward,
  venues,
  isMultiGym,
}: {
  reward: ApiCreditProductAdmin;
  venues: StockVenue[];
  isMultiGym: boolean;
}) => {
  const stockable = venues.filter((venue) => venue.canStock);
  const queries = useCreditProductStockQueries(
    reward.uuid,
    stockable.map((venue) => venue.uuid),
  );
  const saved = new Map<string, ApiCreditProductStock | undefined>(
    stockable.map((venue, index) => [venue.uuid, queries[index].data]),
  );
  const [edits, setEdits] = useState<Record<string, StickerEdit>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const mutation = useSetCreditProductStockMutation(reward);

  // An edit that matches what's saved, such as typing a count and then putting it back, isn't one.
  const changed = Object.entries(edits).filter(
    ([uuid, edit]) =>
      editToRemaining(edit) !== saved.get(uuid)?.remaining ||
      (!edit.isUnlimited && edit.count === null),
  );
  const isDirty = changed.length > 0;
  useUnsavedChanges("stock", isDirty);

  const editFor = (uuid: string): StickerEdit | null => {
    if (edits[uuid]) {
      return edits[uuid];
    }
    const stock = saved.get(uuid);
    return stock ? { count: stock.remaining, isUnlimited: stock.remaining === null } : null;
  };

  const setEdit = (uuid: string, edit: StickerEdit) => {
    setEdits((current) => ({ ...current, [uuid]: edit }));
    setErrors(({ [uuid]: _, ...rest }) => rest);
  };

  const save = async () => {
    const missing = changed.filter(([, edit]) => !edit.isUnlimited && edit.count === null);
    if (missing.length) {
      setErrors(
        Object.fromEntries(missing.map(([uuid]) => [uuid, "Enter a count, or tick Unlimited"])),
      );
      return;
    }
    setIsSaving(true);
    const results = await Promise.allSettled(
      changed.map(([venueUuid, edit]) =>
        mutation.mutateAsync({ venueUuid, remaining: editToRemaining(edit) }),
      ),
    );
    setIsSaving(false);
    // Saved gyms drop their edit and show the new count; failed ones keep theirs to try again.
    const failed = changed.filter((_, index) => results[index].status === "rejected");
    setEdits(Object.fromEntries(failed));
    setErrors(Object.fromEntries(failed.map(([uuid]) => [uuid, "Couldn’t save this count"])));
  };

  const renderInput = (venue: StockVenue) => {
    const index = stockable.indexOf(venue);
    const edit = editFor(venue.uuid);
    if (queries[index]?.error) {
      return <Typography color="danger">Couldn’t load</Typography>;
    }
    if (!edit) {
      return <CircularProgress size="sm" />;
    }
    return (
      <StickerCountInput
        edit={edit}
        error={errors[venue.uuid]}
        onChange={(next) => setEdit(venue.uuid, next)}
      />
    );
  };

  const hint = "Goes down by one each time a climber redeems it.";

  return (
    <Stack gap={2}>
      {isMultiGym ? (
        <Stack gap={1}>
          {venues.map((venue) => (
            <Stack key={venue.uuid} gap={0.5}>
              <VenueRow venue={venue}>{renderInput(venue)}</VenueRow>
              {errors[venue.uuid] ? (
                <Typography level="body-sm" color="danger" sx={{ textAlign: "right" }}>
                  {errors[venue.uuid]}
                </Typography>
              ) : null}
            </Stack>
          ))}
          <Typography level="body-sm" textColor="neutral.500">
            {hint}
          </Typography>
        </Stack>
      ) : (
        <FormControl error={Boolean(errors[venues[0].uuid])}>
          <FormLabel>In stock</FormLabel>
          {renderInput(venues[0])}
          <FormHelperText>{errors[venues[0].uuid] ?? hint}</FormHelperText>
        </FormControl>
      )}
      <Button
        sx={{ alignSelf: "flex-start" }}
        disabled={!isDirty}
        loading={isSaving}
        onClick={save}
      >
        Save stock
      </Button>
    </Stack>
  );
};

const WithdrawBatchButton = ({
  reward,
  batch,
}: {
  reward: ApiCreditProductAdmin;
  batch: ApiCreditProductCodeBatch;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const mutation = useWithdrawCreditProductCodeBatchMutation(reward);

  return (
    <>
      <Button size="sm" variant="plain" color="danger" onClick={() => setIsOpen(true)}>
        Withdraw
      </Button>
      <Modal open={isOpen} onClose={() => setIsOpen(false)}>
        <ModalDialog sx={{ maxWidth: 440 }}>
          <DialogTitle>Withdraw {batch.label}?</DialogTitle>
          <DialogContent>
            This deletes its {pluralize(batch.unclaimed, "unclaimed code")}, so climbers can’t get
            them any more.
            {batch.claimed
              ? ` The ${pluralize(batch.claimed, "code")} already claimed stay in redemption history.`
              : null}{" "}
            This can’t be undone.
          </DialogContent>
          {mutation.isError ? <Alert color="danger">Couldn’t withdraw the codes.</Alert> : null}
          <DialogActions>
            <Button
              color="danger"
              loading={mutation.isPending}
              onClick={() => mutation.mutate(batch.uuid, { onSuccess: () => setIsOpen(false) })}
            >
              Withdraw
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
};

const BatchRow = ({
  reward,
  batch,
  showVenueName,
}: {
  reward: ApiCreditProductAdmin;
  batch: ApiCreditProductCodeBatch;
  showVenueName: boolean;
}) => {
  const isWithdrawn = batch.withdrawnAt !== null;
  const counts = isWithdrawn
    ? `Withdrawn ${shortDateFormat(new Date(batch.withdrawnAt!))} · ${batch.claimed} claimed`
    : `${batch.unclaimed} of ${batch.claimed + batch.unclaimed} left`;
  const uploaded = [
    showVenueName ? batch.venue.name : null,
    batch.createdBy?.name,
    shortDateFormat(new Date(batch.createdAt)),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={{
        py: 1,
        borderTop: "1px solid",
        borderColor: "neutral.100",
        opacity: isWithdrawn ? 0.6 : 1,
      }}
    >
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Typography level="title-sm" noWrap>
          {batch.label}
        </Typography>
        <Typography level="body-sm">{counts}</Typography>
        <Typography level="body-xs" textColor="neutral.500" noWrap>
          {uploaded}
        </Typography>
      </Stack>
      {!isWithdrawn && batch.unclaimed > 0 ? (
        <WithdrawBatchButton reward={reward} batch={batch} />
      ) : null}
    </Stack>
  );
};

const uploadSummary = (upload: ApiCreditProductCodeBatchUpload, showVenueName: boolean) =>
  [
    `Added ${pluralize(upload.added, "code")}${showVenueName ? ` at ${upload.venue.name}` : ""}`,
    upload.skipped ? `${upload.skipped} skipped, as they’d already been added` : null,
  ]
    .filter(Boolean)
    .join(" · ");

// Codes are uploaded in batches at each gym, and a climber who redeems the reward gets one.
const CodeStock = ({
  reward,
  venues,
  isMultiGym,
}: {
  reward: ApiCreditProductAdmin;
  venues: StockVenue[];
  isMultiGym: boolean;
}) => {
  const batchesQuery = useCreditProductCodeBatchesQuery(reward.uuid);
  const [addingAt, setAddingAt] = useState<StockVenue | null>(null);
  const [lastUpload, setLastUpload] = useState<ApiCreditProductCodeBatchUpload | null>(null);

  if (batchesQuery.error) {
    return <Alert color="danger">Couldn’t load the codes.</Alert>;
  }
  const batches = batchesQuery.data;
  if (!batches) {
    return <CircularProgress sx={{ alignSelf: "center" }} />;
  }

  // Counted from the batches, as there's no count for every gym at once. A withdrawn batch has no
  // unclaimed codes left.
  const countAt = (venueUuid: string) =>
    batches
      .filter((batch) => batch.venue.uuid === venueUuid)
      .reduce((total, batch) => total + batch.unclaimed, 0);

  const addButton = (venue: StockVenue) => (
    <Button
      size="sm"
      variant={isMultiGym ? "outlined" : "solid"}
      startDecorator={<Add />}
      onClick={() => setAddingAt(venue)}
    >
      Add codes
    </Button>
  );

  let summary;
  if (isMultiGym) {
    summary = (
      <Stack gap={1}>
        {venues.map((venue) => {
          const count = countAt(venue.uuid);
          return (
            <VenueRow key={venue.uuid} venue={venue}>
              <Typography level="body-sm" color={count ? undefined : "danger"}>
                {count ? pluralize(count, "code") : "No codes"}
              </Typography>
              {addButton(venue)}
            </VenueRow>
          );
        })}
      </Stack>
    );
  } else {
    const venue = venues[0];
    const count = countAt(venue.uuid);
    const hasHadCodes = batches.some((batch) => batch.venue.uuid === venue.uuid);
    summary = (
      <Stack gap={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
          <Typography level="title-lg">
            {count ? `${pluralize(count, "code")} left` : "No codes"}
          </Typography>
          {addButton(venue)}
        </Stack>
        {!count ? (
          <Typography level="body-sm" textColor="neutral.500">
            {hasHadCodes
              ? "Every code has been claimed. Climbers see this reward as sold out until you add more."
              : "No codes yet. Climbers see this reward as sold out until you add some."}
          </Typography>
        ) : null}
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      {summary}
      {lastUpload ? (
        <Alert
          color="success"
          endDecorator={
            <IconButton size="sm" color="success" onClick={() => setLastUpload(null)}>
              <Close />
            </IconButton>
          }
        >
          {uploadSummary(lastUpload, isMultiGym)}
        </Alert>
      ) : null}
      {batches.length ? (
        <Stack>
          <Typography level="title-sm" sx={{ pb: 0.5 }}>
            Batches
          </Typography>
          {batches.map((batch) => (
            <BatchRow key={batch.uuid} reward={reward} batch={batch} showVenueName={isMultiGym} />
          ))}
        </Stack>
      ) : null}
      {addingAt ? (
        <AddCodesModal
          reward={reward}
          venue={addingAt}
          showVenueName={isMultiGym}
          onClose={() => setAddingAt(null)}
          onAdded={(upload) => {
            setAddingAt(null);
            setLastUpload(upload);
          }}
        />
      ) : null}
    </Stack>
  );
};

// The stock of a saved reward at each gym it's offered at: a count for a `STICKER` system, or
// uploaded codes for a `CODE` one. Changes save straight away, apart from the counts, which have
// their own Save. A system with one gym never names it.
export default function RewardStock({
  reward,
  system,
  venueUuid,
}: {
  reward: ApiCreditProductAdmin;
  system: ApiCreditSystemAdmin;
  venueUuid: string;
}) {
  const venues = useStockVenues(reward, system, venueUuid);
  const isMultiGym = system.venues.length > 1;

  let content;
  if (!venues) {
    content = <CircularProgress sx={{ alignSelf: "center" }} />;
  } else if (system.fulfilmentType === "STICKER") {
    content = <StickerStock reward={reward} venues={venues} isMultiGym={isMultiGym} />;
  } else {
    content = <CodeStock reward={reward} venues={venues} isMultiGym={isMultiGym} />;
  }

  return <Stack sx={{ p: 2, flex: 1, minHeight: 0, overflowY: "auto" }}>{content}</Stack>;
}
